// Right-column editor for Models → Uses (create + edit + members).
import {
  ghostBtnClass,
  mfgMdInputClass,
  mfgPrimaryBtnClass,
} from "@/components/manufacturing/manufacturingSurfaceClasses";
import type {
  MfgMaterial,
  MfgMaterialUse,
  MfgMaterialUseMember,
  MfgMaterialUseResolveMode,
  MfgPlant,
  MfgRecipeSummary,
} from "@/lib/manufacturing/types";
import { ManufacturingMaterialUseExplainer } from "./ManufacturingMaterialUseExplainer";
import { ManufacturingMaterialUseDeleteControls } from "./ManufacturingMaterialUseDeleteControls";
import { ManufacturingMaterialUseMemberRow } from "./ManufacturingMaterialUseMemberRow";
import { ManufacturingMaterialUsePromoteLines } from "./ManufacturingMaterialUsePromoteLines";
import { ManufacturingMaterialUseResolveModeFields } from "./ManufacturingMaterialUseResolveModeFields";
import { ManufacturingMaterialUseAddMembersBlock } from "./ManufacturingMaterialUseAddMembersBlock";

type CreateFields = {
  newName: string;
  setNewName: (v: string) => void;
  newCode: string;
  setNewCode: (v: string) => void;
  setNewCodeTouched: (v: boolean) => void;
  newUnit: string;
  setNewUnit: (v: string) => void;
  newNotes: string;
  setNewNotes: (v: string) => void;
  newResolveMode: MfgMaterialUseResolveMode;
  setNewResolveMode: (v: MfgMaterialUseResolveMode) => void;
  newFinishChannel: string;
  setNewFinishChannel: (v: string) => void;
  saveCreate: () => void;
  closeEditor: () => void;
};

type EditFields = {
  editName: string;
  setEditName: (v: string) => void;
  editCode: string;
  setEditCode: (v: string) => void;
  editUnit: string;
  setEditUnit: (v: string) => void;
  editNotes: string;
  setEditNotes: (v: string) => void;
  editActive: boolean;
  setEditActive: (v: boolean) => void;
  editResolveMode: MfgMaterialUseResolveMode;
  setEditResolveMode: (v: MfgMaterialUseResolveMode) => void;
  editFinishChannel: string;
  setEditFinishChannel: (v: string) => void;
  variantOnHandQty: number | null;
  openDeficitCount: number;
  saveEdit: () => void;
  selectedUse: MfgMaterialUse | null;
  members: MfgMaterialUseMember[];
  detailBusy: boolean;
  plantId: string;
  setPlantId: (v: string) => void;
  plants: MfgPlant[];
  plantMaterials: readonly MfgMaterial[];
  recipes: readonly MfgRecipeSummary[];
  memberMaterialIds: ReadonlySet<string>;
  convertMemberMaterialIds: readonly string[];
  onPendingConvertChange?: (pendingLines: number) => void;
  addMember: (materialId: string) => void;
  addMembers: (materialIds: ReadonlyArray<string>) => void;
  updateMember: (
    memberId: string,
    patch: {
      qty_per_use?: number;
      preference_rank?: number;
      is_active?: boolean;
      is_primary?: boolean;
      finish_code?: string | null;
    }
  ) => void;
  removeMember: (memberId: string) => void;
  canDelete?: boolean;
  deletePending?: {
    use: MfgMaterialUse;
    busy: boolean;
    error: string | null;
    blockers: Array<{ code: string; message: string }>;
  } | null;
  onRequestDelete?: (use: MfgMaterialUse) => void;
  onConfirmDelete?: () => void;
  onCancelDelete?: () => void;
};

export function ManufacturingMaterialUseDetailEditor({
  creating,
  editorOpen,
  saveBusy,
  localError,
  onError,
  create,
  edit,
}: {
  creating: boolean;
  editorOpen: boolean;
  saveBusy: boolean;
  localError: string | null;
  onError?: (message: string | null) => void;
  create: CreateFields;
  edit: EditFields;
}) {
  if (!editorOpen) {
    return (
      <p className="text-sm text-gray-500 dark:text-zinc-400">
        Select a use from the list, or create one. Easiest path: Inventory → select alternatives →
        Make interchangeable.
      </p>
    );
  }

  const finishVariant = creating
    ? create.newResolveMode === "finish_variant"
    : edit.editResolveMode === "finish_variant";

  if (creating) {
    return (
      <div className="space-y-3">
        <ManufacturingMaterialUseExplainer variant="uses-create" />
        <label className="block text-sm">
          <span className="font-medium text-gray-700 dark:text-zinc-300">Job name</span>
          <input
            className={mfgMdInputClass + " mt-1 w-full"}
            value={create.newName}
            placeholder="e.g. Doorknob"
            onChange={(e) => create.setNewName(e.target.value)}
            disabled={saveBusy}
          />
        </label>
        <label className="block text-sm">
          <span className="font-medium text-gray-700 dark:text-zinc-300">Use code</span>
          <input
            className={mfgMdInputClass + " mt-1 w-full font-mono"}
            value={create.newCode}
            onChange={(e) => {
              create.setNewCodeTouched(true);
              create.setNewCode(e.target.value);
            }}
            disabled={saveBusy}
          />
        </label>
        <ManufacturingMaterialUseResolveModeFields
          mode={create.newResolveMode}
          setMode={create.setNewResolveMode}
          channel={create.newFinishChannel}
          setChannel={create.setNewFinishChannel}
          disabled={saveBusy}
        />
        <label className="block text-sm">
          <span className="font-medium text-gray-700 dark:text-zinc-300">BOM unit</span>
          <input
            className={mfgMdInputClass + " mt-1 w-full"}
            value={create.newUnit}
            onChange={(e) => create.setNewUnit(e.target.value)}
            disabled={saveBusy}
          />
        </label>
        <label className="block text-sm">
          <span className="font-medium text-gray-700 dark:text-zinc-300">Notes</span>
          <textarea
            className={mfgMdInputClass + " mt-1 w-full"}
            rows={2}
            value={create.newNotes}
            onChange={(e) => create.setNewNotes(e.target.value)}
            disabled={saveBusy}
          />
        </label>
        <div className="flex gap-2">
          <button
            type="button"
            className={mfgPrimaryBtnClass}
            disabled={
              saveBusy ||
              !create.newCode.trim() ||
              !create.newName.trim() ||
              (create.newResolveMode === "finish_variant" && !create.newFinishChannel.trim())
            }
            onClick={() => void create.saveCreate()}
          >
            {saveBusy ? "Saving…" : "Create use"}
          </button>
          <button type="button" className={ghostBtnClass} disabled={saveBusy} onClick={create.closeEditor}>
            Cancel
          </button>
        </div>
        {localError ? <p className="text-sm text-red-700 dark:text-red-300">{localError}</p> : null}
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <div className="grid gap-3 sm:grid-cols-2">
        <label className="block text-sm sm:col-span-2">
          <span className="font-medium text-gray-700 dark:text-zinc-300">Job name</span>
          <input
            className={mfgMdInputClass + " mt-1 w-full"}
            value={edit.editName}
            onChange={(e) => edit.setEditName(e.target.value)}
            disabled={saveBusy || edit.detailBusy}
          />
        </label>
        <label className="block text-sm">
          <span className="font-medium text-gray-700 dark:text-zinc-300">Use code</span>
          <input
            className={mfgMdInputClass + " mt-1 w-full font-mono"}
            value={edit.editCode}
            onChange={(e) => edit.setEditCode(e.target.value)}
            disabled={saveBusy || edit.detailBusy}
          />
        </label>
        <label className="block text-sm">
          <span className="font-medium text-gray-700 dark:text-zinc-300">BOM unit</span>
          <input
            className={mfgMdInputClass + " mt-1 w-full"}
            value={edit.editUnit}
            onChange={(e) => edit.setEditUnit(e.target.value)}
            disabled={saveBusy || edit.detailBusy}
          />
        </label>
        <ManufacturingMaterialUseResolveModeFields
          mode={edit.editResolveMode}
          setMode={edit.setEditResolveMode}
          channel={edit.editFinishChannel}
          setChannel={edit.setEditFinishChannel}
          disabled={saveBusy || edit.detailBusy}
        />
        <label className="block text-sm sm:col-span-2">
          <span className="font-medium text-gray-700 dark:text-zinc-300">Notes</span>
          <textarea
            className={mfgMdInputClass + " mt-1 w-full"}
            rows={2}
            value={edit.editNotes}
            onChange={(e) => edit.setEditNotes(e.target.value)}
            disabled={saveBusy || edit.detailBusy}
          />
        </label>
        <label className="flex items-center gap-2 text-sm">
          <input
            type="checkbox"
            checked={edit.editActive}
            onChange={(e) => edit.setEditActive(e.target.checked)}
            disabled={saveBusy || edit.detailBusy}
          />
          <span className="font-medium text-gray-700 dark:text-zinc-300">Active</span>
        </label>
      </div>

      {finishVariant ? (
        <div className="rounded-md border border-amber-200 bg-amber-50 px-3 py-2 text-xs text-amber-900 dark:border-amber-800 dark:bg-amber-950/40 dark:text-amber-100">
          Plant variant on hand:{" "}
          <strong>
            {edit.variantOnHandQty == null ? "—" : edit.variantOnHandQty.toLocaleString()}
          </strong>
          {" · "}
          Open deficits: <strong>{edit.openDeficitCount}</strong>
        </div>
      ) : null}

      <div className="flex flex-wrap items-center gap-2">
        <button
          type="button"
          className={mfgPrimaryBtnClass}
          disabled={saveBusy || edit.detailBusy}
          onClick={() => void edit.saveEdit()}
        >
          {saveBusy ? "Saving…" : "Save use"}
        </button>
        {edit.canDelete && edit.selectedUse && edit.onRequestDelete && edit.onConfirmDelete && edit.onCancelDelete ? (
          <ManufacturingMaterialUseDeleteControls
            use={edit.selectedUse}
            disabled={saveBusy || edit.detailBusy}
            pending={edit.deletePending ?? null}
            onRequest={edit.onRequestDelete}
            onConfirm={edit.onConfirmDelete}
            onCancel={edit.onCancelDelete}
          />
        ) : null}
      </div>

      <div className="border-t border-gray-100 pt-4 dark:border-zinc-800">
        {edit.selectedUse && !finishVariant ? (
          <ManufacturingMaterialUsePromoteLines
            use={edit.selectedUse}
            materials={edit.plantMaterials}
            recipes={edit.recipes}
            memberMaterialIds={edit.convertMemberMaterialIds}
            onPendingConvertChange={edit.onPendingConvertChange}
          />
        ) : null}
        <div className="mb-3 flex flex-wrap items-end gap-3">
          <label className="block text-sm">
            <span className="font-medium text-gray-700 dark:text-zinc-300">Plant filter</span>
            <select
              className={mfgMdInputClass + " mt-1 min-w-[12rem]"}
              value={edit.plantId}
              onChange={(e) => edit.setPlantId(e.target.value)}
              disabled={edit.plants.length === 0}
            >
              {edit.plants.map((p) => (
                <option key={p.id} value={p.id}>
                  {p.name} ({p.plant_code})
                </option>
              ))}
            </select>
          </label>
        </div>

        <p className="mb-2 text-xs text-gray-600 dark:text-zinc-400">
          {finishVariant
            ? "Members are color/finish SKUs. Finish codes save on blur; set one primary (estimate only)."
            : "Members are interchangeable SKUs. Pref 1 is tried first; qty/use multiplies BOM qty."}
        </p>

        {edit.detailBusy ? (
          <p className="text-sm text-gray-500 dark:text-zinc-400">Loading members…</p>
        ) : (
          <div className="overflow-x-auto rounded-lg border border-gray-200 dark:border-zinc-700">
            <table className="min-w-full text-left">
              <thead className="bg-gray-50 text-xs font-semibold uppercase text-gray-600 dark:bg-zinc-800 dark:text-zinc-400">
                <tr>
                  <th className="px-2 py-2">Material</th>
                  <th className="px-2 py-2">Unit</th>
                  <th className="px-2 py-2">On hand</th>
                  <th className="px-2 py-2">{finishVariant ? "Finish" : "Pref."}</th>
                  <th className="px-2 py-2">Qty/use</th>
                  <th className="px-2 py-2 text-right">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100 dark:divide-zinc-800">
                {edit.members.length === 0 ? (
                  <tr>
                    <td colSpan={6} className="px-2 py-3 text-sm text-gray-500 dark:text-zinc-400">
                      No members at this plant yet.
                    </td>
                  </tr>
                ) : (
                  edit.members.map((m) => (
                    <ManufacturingMaterialUseMemberRow
                      key={m.id}
                      member={m}
                      finishVariant={finishVariant}
                      disabled={saveBusy}
                      onSave={(patch) => void edit.updateMember(m.id, patch)}
                      onRemove={() => {
                        if (
                          !window.confirm(
                            `Remove ${m.material_code} from this Part use? This cannot be undone (you can re-add later).`,
                          )
                        ) {
                          return;
                        }
                        void edit.removeMember(m.id);
                      }}
                      onSetPrimary={() => void edit.updateMember(m.id, { is_primary: true })}
                    />
                  ))
                )}
              </tbody>
            </table>
          </div>
        )}

        <ManufacturingMaterialUseAddMembersBlock
          selectedUse={edit.selectedUse}
          plantMaterials={edit.plantMaterials}
          memberMaterialIds={edit.memberMaterialIds}
          disabled={saveBusy || !edit.plantId || edit.detailBusy}
          onAddMembers={edit.addMembers}
          onError={onError}
        />
      </div>
      {localError ? <p className="mt-3 text-sm text-red-700 dark:text-red-300">{localError}</p> : null}
    </div>
  );
}
